import { useState, useEffect, useCallback } from 'react';
import './VoiceInput.css'; // Import the CSS file for styling

const VoiceInput = ({ onQuestionSubmit }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  
  // Initialize speech recognition with broader browser support
  const createRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || 
                             window.webkitSpeechRecognition || 
                             window.mozSpeechRecognition || 
                             window.msSpeechRecognition;
    
    if (!SpeechRecognition) {
      setError('Speech recognition is not supported in this browser. Please try Chrome or Safari.');
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US'; // You can make this configurable

    // Add error handling
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setError(`Microphone error: ${event.error}. Please check microphone permissions.`);
      setIsListening(false);
    };

    return recognition;
  }, []);

  useEffect(() => {
    const recognition = createRecognition();
    if (!recognition) return;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setTranscript(transcript);
      setError(''); // Clear any previous errors
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    if (isListening) {
      try {
        recognition.start();
      } catch (err) {
        console.error('Error starting recognition:', err);
        setError('Failed to start microphone. Please try again.');
        setIsListening(false);
      }
    }

    return () => {
      try {
        recognition.stop();
      } catch (err) {
        console.error('Error stopping recognition:', err);
      }
    };
  }, [isListening, createRecognition]);

  // Check for microphone permissions
  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setError('');
      return true;
    } catch (err) {
      setError('Microphone permission denied. Please allow microphone access.');
      return false;
    }
  };

  const handleStart = async () => {
    const hasPermission = await checkMicrophonePermission();
    if (hasPermission) {
      setTranscript('');
      setIsListening(true);
    }
  };

  const handleSubmit = () => {
    if (transcript) {
      onQuestionSubmit(transcript);
      setTranscript('');
    }
  };

  return (
    <div className="voice-input">
      {error && (
        <p className="error-message" style={{ color: '#ff4444', marginBottom: '10px' }}>
          {error}
        </p>
      )}
      <p className="microphone-status">
        {isListening ? 'Listening...' : 'Click Start to speak'}
      </p>
      <button 
        className="voice-button"
        onClick={handleStart}
        disabled={isListening}
      >
        {isListening ? 'Listening...' : 'Start'}
      </button>
      {transcript && (
        <>
          <div className="transcription-container">
            <p className="transcription-text">{transcript}</p>
          </div>
          <button className="voice-button" onClick={handleSubmit}>
            Submit
          </button>
        </>
      )}
    </div>
  );
};

export default VoiceInput;